<template>
  <CCard v-if="!isLoading" class="position-relative">
    <UnsavedChangesIndicator
      :visible="hasFormChanges"
      :isDisabled="submitted"
      @save-click="submit"
    />

    <CCardHeader>
      <CIcon name="cil-notes"/>
      <h5 class="d-inline ml-2">
        {{ this.$route.meta.label }}
        <template v-if="isEdit">
          : <h3 class="d-inline"><CBadge color="secondary">{{ this.itemName }} | {{ this.restaurantName }}</CBadge></h3>
        </template>
        <template v-else>
          to <h3 class="d-inline"><CBadge color="secondary">{{ this.restaurantName }}</CBadge></h3>
          Restaurant
        </template>
      </h5>
      <div class="card-header-actions d-flex flex-gap-2">
        <CButton color="info" :to="{ name: 'OptionGroups', params: { 'restaurant_id': restaurantId } }" class="d-flex align-items-center flex-gap-1">
          <CIcon name="cil-list-rich" class="align-bottom" />Manage Option Groups
        </CButton>

        <CButton color="dark" :to="this.prevRoute">
          <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
        </CButton>
      </div>
    </CCardHeader>

    <CCardBody>
      <alert-section
        :successAlertMessage="successAlertMessage"
        :dismissSecs="dismissSecs"
        :dismissSuccessAlert="dismissSuccessAlert"
        :errorAlertMessage="errorAlertMessage"
        :showErrorAlert="showErrorAlert"
      />

      <CForm>
        <CRow>
          <CCol col="12" sm="6">
            <CSelect
              label="Menu Category"
              :options="availableCategories"
              :value.sync="$v.form.menu_cat.$model"
              placeholder="Please choose a category"
              custom
              :isValid="$globalFunc.checkIfValid($v.form, 'menu_cat')"
              @update:value="changedMenuCategory"
            />
          </CCol>

          <CCol col="12" sm="6">
            <CSelect
              label="Item Type"
              :options="this.$globalVar.menuTypes"
              :value.sync="$v.form.type_id.$model"
              placeholder="Please choose a item type"
              custom
              :isValid="$globalFunc.checkIfValid($v.form, 'type_id')"
            />
          </CCol>

          <CCol col="12" v-if="menuNames">
            <div class="alert alert-success" role="alert">
              <h5 class="alert-heading">Menus in this category:</h5>
              {{ menuNames }}
            </div>
          </CCol>

          <CCol col="12" sm="6">
            <CInput
              label="Name"
              placeholder="Name"
              :lazy="false"
              :value.sync="$v.form.menu_name.$model"
              :isValid="$globalFunc.checkIfValid($v.form, 'menu_name')"
              invalidFeedback="This is a required field and must be at least 2 characters"
            />
          </CCol>

          <CCol col="12" sm="6">
            <CInput
              label="Custom ID"
              placeholder="Custom ID"
              :lazy="false"
              :value.sync="$v.form.custom_id.$model"
            />
          </CCol>

          <CCol col="12">
            <CTextarea
              label="Description"
              placeholder="Description"
              rows="2"
              :lazy="false"
              :value.sync="$v.form.description.$model"
            />
          </CCol>

          <CCol col="12" sm="6">
            <CInput
              :label="hasVariants ? 'Starting Price' : 'Price'"
              placeholder="0.00"
              :lazy="false"
              :value.sync="$v.form.price.$model"
              :isValid="$globalFunc.checkIfValid($v.form, 'price')"
              invalidFeedback="This is a required field and must be numeric"
              type="number"
              step="0.01"
              pattern="^\d+(?:\.\d{1,2})?$"
            >
              <template #prepend-content><CIcon name="cil-euro"/></template>
            </CInput>
          </CCol>

          <CCol col="12" sm="6">
            <CInput
              v-if="this.usetoprice === true"
              :label="hasVariants ? 'Starting Takeout Price' : 'Takeout Price'"
              placeholder="0.00"
              :lazy="false"
              :value.sync="$v.form.toPrice.$model"
              :isValid="$globalFunc.checkIfValid($v.form, 'toPrice')"
              invalidFeedback="This is a required field and must be numeric"
              type="number"
              step="0.01"
              pattern="^\d+(?:\.\d{1,2})?$"
            >
              <template #prepend-content><CIcon name="cil-euro"/></template>
            </CInput>
          </CCol>

          <CCol col="12" sm="6">
            <div class="d-flex" style="gap: 3rem">
              <div class="form-group">
                <label class="d-block" style="font-weight: 500">Sold Out</label>
                <CSwitch
                  labelOn="Yes"
                  labelOff="No"
                  color="danger"
                  shape="pill"
                  :lazy="false"
                  :checked.sync="$v.form.sold_out.$model"
                />
              </div>
            </div>
          </CCol>

          <CCol col="12" sm="6">
            <div class="d-flex flex-column">
              <CMedia
                :aside-image-props="{
                  src: this.previewMenuImage,
                  shape: 'rounded',
                  width: '100%',
                  height: '120px'
                }"
              >
                <p class="mb-2 font-weight-bold">Menu Image:</p>
                <CInput
                  :value.sync="$v.form.menu_image.$model"
                  type="hidden"
                  class="mb-0"
                />
                <label for="img" class="btn btn-outline-info mb-0">
                  <CIcon name="cil-file" /> Browse
                </label>

                <CInputFile
                  id="img"
                  :class="!$globalFunc.checkIfValid($v.form, 'menu_image') ? 'is-invalid' : ''"
                  style="display: none"
                  @change = "onFileChanged"
                />

                <div v-if="!$globalFunc.checkIfValid($v.form, 'menu_image')" class="invalid-feedback">
                  <div class="error" v-if="!$v.form.menu_image.required">
                    <span style="font-size: 1.2rem;">⚠️</span> Please select an image for the menu!
                  </div>
                </div>
              </CMedia>

              <CLink v-if="previewMenuImage"
                class="small mt-2 text-danger"
                @click="() => { this.selectedMenuImage = this.previewMenuImage = null; this.form.menu_image = ''; }"
              >
                <CIcon name="cil-trash" /> Remove Image
              </CLink>
            </div>
          </CCol>
        </CRow>

        <CRow>
          <CCol col="12" class="mt-2">
            <div class="variant-selector mb-4">
              <h5 class="d-flex align-items-center">
                <span>Menu Variants</span>
                <CBadge color="info" class="ml-2">{{ form.variants ? form.variants.length : 0 }}</CBadge>
                <CButton color="primary" size="sm" class="ml-auto" @click="openVariantManagement">
                  <CIcon name="cil-settings" /> Manage Variants
                </CButton>
              </h5>

              <div class="variant-tabs">
                <div
                  v-if="!hasVariants"
                  class="variant-tab"
                  :class="{'active': selectedVariantId === null}"
                  @click="selectVariant(null)"
                >
                  <div class="d-flex justify-content-between align-items-start flex-gap-1">
                    <span>Default Menu</span>
                    <CBadge color="light" class="price-badge">{{ parseFloat(form.price || 0) | toCurrency }}</CBadge>
                  </div>
                </div>

                <div
                  v-for="variant in form.variants"
                  :key="variant.id"
                  class="variant-tab"
                  :class="{'active': selectedVariantId === variant.id, 'inactive': variant.sold_out}"
                  @click="selectVariant(variant.id)"
                >
                  <div class="d-flex justify-content-between align-items-start flex-gap-1">
                    <div class="d-flex flex-column">
                      <span>{{ variant.name }}</span>
                      <small v-if="variant.sold_out" class="text-danger">(Sold Out)</small>
                      <small v-if="variant.description" class="text-muted description">{{ variant.description | truncate(60) }}</small>
                    </div>
                    <div class="d-flex flex-column align-items-end flex-gap-1">
                      <CBadge class="price-badge" :color="parseFloat(variant.price_adjustment) > 0 ? 'success' : 'light'">
                        {{ parseFloat(variant.price) | toCurrency }}
                        <small v-if="variant.price_adjustment != 0">({{ parseFloat(variant.price_adjustment) | toCurrency }})</small>
                      </CBadge>

                      <CBadge v-if="usetoprice" class="price-badge" color="light" v-c-tooltip="{content: 'Takeout Price', placement: 'bottom'}">
                        {{ parseFloat(variant.toPrice) | toCurrency }}
                        <small v-if="variant.toPrice_adjustment != 0">({{ parseFloat(variant.toPrice_adjustment) | toCurrency }})</small>
                      </CBadge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CCol>
        </CRow>

        <CRow>
          <CCol col="12" class="mt-2">
            <div class="option-groups-section">
              <!-- Sticky Header -->
              <div class="option-groups-header d-flex flex-column flex-xxl-row justify-content-between align-items-xxl-center align-items-start flex-gap-3 p-2">
                <div class="d-flex flex-column flex-sm-row align-items-sm-center flex-grow-1 flex-gap-3 w-xl-100">
                  <h5 class="mb-0">
                    {{ selectedVariantId === null ? 'Default Menu' : getSelectedVariantName() }} Option Groups
                  </h5>

                  <!-- Option Group Templates Component -->
                  <OptionGroupTemplates
                    class="flex-grow-1"
                    :restaurantId="restaurantId"
                    :selectedVariantId="selectedVariantId"
                    :selectedMenuOptionGroups="form.selectedMenuOptionGroups"
                    :variants="form.variants"
                    @apply-template="handleApplyTemplate"
                    @templates-loaded="handleTemplatesLoaded"
                    ref="optionGroupTemplates"
                  />
                </div>

                <div class="d-flex flex-md-row flex-column align-items-md-center align-items-start flex-gap-2 w-xl-100">
                  <div class="d-flex flex-gap-2">
                    <CDropdown
                      color="primary"
                      size="sm"
                      v-if="canCloneOptionGroups"
                    >
                      <template #toggler>
                        <CButton color="primary" size="sm">
                          <CIcon name="cil-clone" class="mr-1" /> Clone Option Groups
                        </CButton>
                      </template>
                      <CDropdownItem @click="cloneOptionGroups(null)" v-if="selectedVariantId !== null && !hasVariants">
                        From <strong class="ml-1">Default Menu</strong>
                      </CDropdownItem>
                      <CDropdownItem
                        v-for="variant in form.variants.filter(v => v.id !== selectedVariantId)"
                        :key="'clone-' + variant.id"
                        @click="cloneOptionGroups(variant.id)"
                      >
                        From <strong class="ml-1">{{ variant.name }}</strong>
                      </CDropdownItem>
                    </CDropdown>

                    <CButton
                      size="sm"
                      variant="ghost"
                      color="primary"
                      @click="toggleAllOptionGroups"
                    >
                      <CIcon :name="showAllOptions ? 'cil-chevron-top' : 'cil-chevron-bottom'" />
                      {{ showAllOptions ? 'Hide All Options' : 'Show All Options' }}
                    </CButton>

                    <CButton
                      size="sm"
                      variant="ghost"
                      color="dark"
                      @click="toggleOptionIds"
                    >
                      <CIcon name="cil-code" />
                      {{ showOptionIds ? 'Hide IDs' : 'Show IDs' }}
                    </CButton>
                  </div>

                  <div v-if="form.variants && form.variants.length > 0" class="ml-auto">
                    <CBadge
                      v-for="variant in form.variants"
                      :key="'badge-' + variant.id"
                      :color="selectedVariantId === variant.id ? 'primary' : 'light'"
                      class="mr-1"
                    >
                      {{ variant.name }}: {{ getMenuOptionGroupsForVariant(variant.id).length }}
                    </CBadge>
                  </div>

                  <CBadge v-else :color="selectedVariantId === null ? 'primary' : 'light'" class="ml-auto">
                    Default: {{ getMenuOptionGroupsForVariant(null).length }}
                  </CBadge>
                </div>
              </div>

              <!-- Table with Option Groups -->
              <div class="table-responsive option-groups-content">
                <table class="table">
                  <thead>
                    <tr>
                      <th scope="col" style="min-width: 52px; width: 52px"></th>
                      <th scope="col" style="min-width: 52px; width: 52px"><CIcon name="cil-move" /></th>
                      <th scope="col" style="min-width: 320px; width: 320px">Option Groups</th>
                      <th scope="col" style="min-width: 400px;">Options</th>
                    </tr>
                  </thead>

                  <tbody ref="optionGroupsTbody">
                    <tr v-for="og in allOptionGroups" :key="og.id" :class="isSelectedOptionGroup(og.id) ? 'selected-item': 'disable-item'">
                      <td class="enable-item">
                        <CInputCheckbox
                          :key="'option-group-checkbox-' + selectedVariantId + '-' + og.id + '-' + (isSelectedOptionGroup(og.id) ? 'selected' : 'unselected')"
                          addLabelClasses="custom-checkbox-label"
                          custom
                          @update:checked="(value) => selectOptionGroup(og.id, value)"
                          :checked="isSelectedOptionGroup(og.id)"
                        />
                      </td>

                      <td class="move-item">
                        <CIcon
                          name="cil-move"
                          :data-smog-id="getSelectedMenuOptionGroupId(og.id)"
                          :data-og-id="og.id"
                          class="align-bottom"
                          size="lg"
                        />
                      </td>

                      <td style="vertical-align: top !important;">
                        <h5>{{og.name}}</h5>
                        <small>{{og.description | truncate(120)}}</small>

                        <section class="border-top pt-3" v-if="isSelectedOptionGroup(og.id)">
                          <div class="form-group mb-0">
                            <label class="d-block">Valid Days *</label>
                            <div class="day-group">
                              <div v-for="(day, index) in days" class="form-check form-check-inline mr-1 mt-1">
                                <CInputCheckbox
                                  :label="day"
                                  :checked="selectedValidDays(og.id)?.includes(index)"
                                  @update:checked="updateValidDays(og.id, index)"
                                />
                              </div>
                            </div>
                            <small>* All days are valid if none is selected</small>
                          </div>
                        </section>
                      </td>

                      <td class="enable-item">
                        <!-- Option Group Bilgileri -->
                        <div v-if="isSelectedOptionGroup(og.id)" class="option-group-info mb-3">
                          <div class="d-flex align-items-center flex-gap-1">
                            <span class="badge badge-primary">
                              Min: {{ og.min_selections || 0 }}
                            </span>
                            <span class="badge badge-primary">
                              Max: {{ og.max_selections || 'All' }}
                            </span>
                            <span class="small text-muted">
                              {{ og.max_selections === 1 ? 'Single selection' : 'Multiple selections' }}
                            </span>
                          </div>
                        </div>

                        <!-- Options Listesi -->
                        <div v-if="isSelectedOptionGroup(og.id) && showAllOptions" class="options-container">
                          <div class="row mx-0">
                            <div
                              v-for="(option, index) in og.options"
                              v-bind:key="option.id"
                              class="col-12 col-md-6 col-lg-4 mb-2 px-1"
                            >
                              <div class="option-item p-2" :class="{'option-inactive': option.sold_out}">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div>
                                    <span :class="{'text-muted': option.sold_out}">{{ option.name }}</span>
                                    <span v-if="showOptionIds" class="badge badge-dark ml-2">ID: {{ option.id }}</span>
                                    <span v-if="option.sold_out" class="badge badge-danger ml-2">Sold Out</span>
                                  </div>

                                  <div>
                                    <!-- Varyant için özel fiyat varsa göster -->
                                    <div
                                      v-if="
                                        selectedVariantId &&
                                        variantOptionPriceLookup[og.id] &&
                                        variantOptionPriceLookup[og.id][option.id] &&
                                        variantOptionPriceLookup[og.id][option.id].price
                                      "
                                    >
                                      <del class="badge badge-secondary">{{ parseFloat(option.price) | toCurrency }}</del>
                                      <span class="badge ml-1" :class="variantOptionPriceLookup[og.id][option.id].priceClass">
                                        {{ variantOptionPriceLookup[og.id][option.id].price | toCurrency }}
                                        <CLink
                                          class="badge badge-info ml-1"
                                          @click="openVariantOptionPriceModal(option)"
                                        >
                                          <CIcon name="cil-pencil" size="sm" />
                                        </CLink>
                                      </span>
                                    </div>

                                    <span v-else class="badge" :class="option.price > 0 ? 'badge-success' : option.price < 0 ? 'badge-danger' : 'badge-light'">
                                      {{ parseFloat(option.price) | toCurrency }}
                                      <CLink
                                        v-if="selectedVariantId !== null"
                                        class="badge badge-info ml-1"
                                        @click="openVariantOptionPriceModal(option)"
                                      >
                                        <CIcon name="cil-pencil" size="sm" />
                                      </CLink>
                                    </span>
                                  </div>
                                </div>
                                <small v-if="option.description" class="text-muted d-block mt-1">{{ option.description }}</small>
                              </div>
                            </div>
                          </div>
                          <div v-if="og.options.length == 0" class="alert alert-warning py-2">
                            <strong>No options found!</strong>
                          </div>
                        </div>
                        <div v-else-if="isSelectedOptionGroup(og.id) && !showAllOptions" class="text-muted">
                          <div class="p-2 border rounded bg-light text-center">
                            {{ og.options.length }} option{{ og.options.length !== 1 ? 's' : '' }} available
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </CCol>
        </CRow>
      </CForm>
    </CCardBody>

    <CCardFooter class="sticky-bottom">
      <CButton color="primary" :disabled="submitted" @click="submit()">
        <CIcon name="cil-save" /> Save Menu
      </CButton>
    </CCardFooter>

    <mc-spinner :opacity="0.8" v-show="submitted" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />

    <VariantManagementModal
      :menuId="itemId"
      :menuVariants="form.variants || []"
      :usetoprice="this.usetoprice"
    />

    <VariantOptionPriceModal
      :option="selectedOption"
      :variants="getVariantsWithOptionPrices(selectedOption)"
      :usetoprice="this.usetoprice"
      @variant-option-price-updated="refreshOptionPrices"
    />
  </CCard>
</template>

<script>
import { validationMixin } from "vuelidate"
import { required, minLength } from "vuelidate/lib/validators"
import _debounce from 'lodash/debounce';  // Import Specific Lodash Functions
import Sortable from "sortablejs";
import VariantManagementModal from "./variants/ManagementModal";
import VariantOptionPriceModal from "./variants/VariantOptionPriceModal";
import UnsavedChangesIndicator from '@/components/UnsavedChangesIndicator.vue';
import OptionGroupTemplates from "./components/OptionGroupTemplates.vue";

export default {
  name: 'MenuV2Item',

  components: {
    VariantManagementModal,
    VariantOptionPriceModal,
    UnsavedChangesIndicator,
    OptionGroupTemplates,
  },

  data() {
    return {
      form: {},
      isEdit: false,
      submitted: false,

      itemName: '',
      restaurantName: '',

      selectedMenuImage: undefined,
      previewMenuImage: undefined,

      availableCategories: [],
      allOptionGroups: [],

      // Alert işlemleri için
      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      // Get previous page
      prevRoute: null,

      // Element Cover Spinner için
      mcSpinnerMarginTop: 0,

      // Get Menu Names by Menu Category (changedMenuCategory)
      menuNames: null,
      usetoprice: false,
      isLoading:true,

      days: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
      selectedVariantId: null,

      // Option gruplarını göster/gizle için state
      showAllOptions: false,
      showOptionIds: false,

      // Option seçimi (variant option price modal için) için state
      selectedOption: null,

      // Form değişikliklerini izleme için state
      hasFormChanges: false,
      lastSavedForm: null,
      handleVariantsUpdated: null,
      handleVariantDeleted: null,
      optionGroupsSortable: null,

      // Option group templates
      menuTemplates: [],
      selectedTemplate: null,
      originalOptionGroupState: null,
      isCustomCollection: false,
      appliedTemplateGroups: null,
    }
  },

  // Get previous page
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  async mounted() {
    // F5 veya başka bir siteye gitmeden önce 'Yaptığınız değişiklikler kaydedilmemiş olabilir.' koruması için
    window.onbeforeunload = () => this.hasFormChanges === true ? true : null;

    this.submitted = true;
    await this.getRestaurantName();
    await this.getAvailableCategories();

    if(this.itemId) {
      this.form = await this.getForm(this.itemId);
      this.isEdit = true;

      // Select variant
      this.selectVariant(this.hasVariants ? this.form.variants[0].id : null);
    }
    else {
      this.form = await this.getForm();
      this.$nextTick(() => {
        this.initOptionGroupSortable();
      });
    }

    await this.getAllOptionGroups();

    // Varyant güncellemelerini dinle (debounce ile duplicate event'leri engelle)
    this.handleVariantsUpdated = _debounce(this.refreshVariants, 300);
    this.handleVariantDeleted = _debounce(this.variantDeleted, 300);
    this.$root.$on('variants-updated', this.handleVariantsUpdated);
    this.$root.$on('variant-deleted', this.handleVariantDeleted);
  },

  beforeRouteLeave (to, from , next) {
    if (this.hasFormChanges === true) {
      const answer = window.confirm('Do you really want to leave? You have unsaved changes!')
      this.hasFormChanges = !answer;
      next(answer) // answer: true or false
    } else {
      next()
    }
  },

  beforeDestroy() {
    if (this.optionGroupsSortable) {
      this.optionGroupsSortable.destroy();
      this.optionGroupsSortable = null;
    }

    if (this.handleVariantsUpdated) {
      this.$root.$off('variants-updated', this.handleVariantsUpdated);
    }
    if (this.handleVariantDeleted) {
      this.$root.$off('variant-deleted', this.handleVariantDeleted);
    }
  },

  computed: {
    isValid () { return !this.$v.form.$invalid },
    isDirty () { return this.$v.form.$anyDirty },
    restaurantId() { return this.$route.params.restaurant_id },
    itemId() { return this.$route.params.id },
    hasVariants() { return this.form.variants && this.form.variants.length > 0 },
    canCloneOptionGroups() {
      if (this.selectedVariantId === null) {
        return this.form.variants && this.form.variants.length > 0;
      } else {
        // Default menu veya en az bir başka varyant olmalı
        return true;
      }
    },

    variantOptionPriceLookup() {
      if (!this.selectedVariantId || !this.form || !this.form.selectedMenuOptionGroups) {
        return {};
      }

      return this.getMenuOptionGroupsForVariant(this.selectedVariantId).reduce((lookup, menuOptionGroup) => {
        const optionGroupId = menuOptionGroup.optionGroupId;
        const options = menuOptionGroup.optionGroup?.options || [];

        if (!optionGroupId || options.length === 0) {
          return lookup;
        }

        lookup[optionGroupId] = options.reduce((optionLookup, option) => {
          const optionPrice = option.menu_variant_option_prices?.find(
            price => price.menu_variant_id === this.selectedVariantId
          );

          if (!optionPrice) {
            return optionLookup;
          }

          const price = parseFloat(optionPrice.price);

          optionLookup[option.id] = {
            price,
            priceClass: price > 0 ? 'badge-success' : price < 0 ? 'badge-danger' : 'badge-light'
          };

          return optionLookup;
        }, lookup[optionGroupId] || {});

        return lookup;
      }, {});
    },

    templateOptions() {
      if (!this.menuTemplates || this.menuTemplates.length === 0) {
        return [{ value: null, label: 'Select Option Group Template...' }];
      }

      // Add "None" option at the beginning
      const options = [
        { value: null, label: 'Select Option Group Template...' }
      ];

      // Add all template options
      this.menuTemplates.forEach(template => {
        options.push({
          value: template.id,
          label: template.name,
          optionGroupIds: template.option_group_ids,
          source: template.source
        });
      });

      return options;
    },
  },

  mixins: [validationMixin],

  validations: {
    form: {
      menu_cat: {
        required: (value) => {
          return value !== 0;
        }
      },
      type_id: { },
      menu_name: { required, minLength: minLength(2) },
      description: { },
      price: { required },
      toPrice: { required },
      menu_image: {
        required: (fileName) => {
          if(!fileName) // Image ekleme zorunlu değil
            return true;

          var filename_arr = fileName.split(".");
          var file_ext = filename_arr[filename_arr.length - 1];

          return ["jpeg", "jpg", "png", "gif", "webp"].indexOf(file_ext) !== -1;
        }
      },
      custom_id: { },
      menugroups: { },
      sold_out: { },
      internNotes: { },
    },
  },

  watch: {
    form: {
      handler(newVal) {
        if (this.lastSavedForm) {
          // Compare current form with last saved form to detect changes
          this.hasFormChanges = JSON.stringify(newVal) !== JSON.stringify(this.lastSavedForm);

          // if (this.hasFormChanges) {
          //   console.log('Form changed:', this.getChangedFields(this.lastSavedForm, newVal));
          // }
        }
      },
      deep: true
    }
  },

  methods: {
    //#region Menu Option Groups
    selectOptionGroup(optionGroupId, isSelected) {
      const index = this.form.selectedMenuOptionGroups.findIndex(item =>
        item.optionGroupId === optionGroupId && item.variantId === this.selectedVariantId
      );
      let selectionChanged = false;

      if (isSelected) {
        if (index === -1) {
          // Yeni bir option grup seçildi
          const maxIndex = Math.max(0, ...this.form.selectedMenuOptionGroups
            .filter(item => item.variantId === this.selectedVariantId)
            .map(item => item.Index || 0)
          );

          const nextIndex = maxIndex + 1;
          const optionGroup = this.allOptionGroups.find(og => og.id === optionGroupId);

          this.form.selectedMenuOptionGroups.push({
            optionGroupId,
            Index: nextIndex,
            display_order: nextIndex,
            validDaysOfWeek: [],
            variantId: this.selectedVariantId,
            selected: true,
            optionGroup
          });

          // Option gruba variant_id atama
          if (optionGroup) {
            optionGroup.variant_id = this.selectedVariantId;
          }
          selectionChanged = true;

          // Option gruplarında değişiklik yapıldığında OptionGroupTemplates bileşenini bilgilendir
          if (this.$refs.optionGroupTemplates) {
            this.$refs.optionGroupTemplates.checkIfCustomCollection();
          }
        }
      } else {
        if (index !== -1) {
          // Option grup seçimi kaldırıldı
          this.form.selectedMenuOptionGroups.splice(index, 1);

          // Option gruptan variant_id'yi kaldır
          const optionGroup = this.allOptionGroups.find(og => og.id === optionGroupId);
          if (optionGroup) {
            optionGroup.variant_id = null;
          }
          selectionChanged = true;

          // Option gruplarında değişiklik yapıldığında OptionGroupTemplates bileşenini bilgilendir
          if (this.$refs.optionGroupTemplates) {
            this.$refs.optionGroupTemplates.checkIfCustomCollection();
          }
        }
      }

      if (selectionChanged) {
        this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
      }
    },

    isSelectedOptionGroup(optionGroupId) {
      return this.form.selectedMenuOptionGroups.some(item =>
        item.optionGroupId === optionGroupId && item.variantId === this.selectedVariantId
      );
    },

    selectedValidDays(optionGroupId) {
      const item = this.form.selectedMenuOptionGroups.find(item =>
        item.optionGroupId === optionGroupId && item.variantId === this.selectedVariantId
      );
      return item ? item.validDaysOfWeek : [];
    },

    updateValidDays(optionGroupId, dayIndex) {
      const item = this.form.selectedMenuOptionGroups.find(item =>
        item.optionGroupId === optionGroupId && item.variantId === this.selectedVariantId
      );

      if (item) {
        if (!item.validDaysOfWeek) {
          item.validDaysOfWeek = [];
        }

        const index = item.validDaysOfWeek.indexOf(dayIndex);
        if (index === -1) {
          item.validDaysOfWeek.push(dayIndex);
        } else {
          item.validDaysOfWeek.splice(index, 1);
        }

        // Geçerli günlerin değiştirilmesi de option gruplarında bir değişiklik sayılır
        if (this.$refs.optionGroupTemplates) {
          this.$refs.optionGroupTemplates.checkIfCustomCollection();
        }
      }
    },

    prepareOptionGroups(menuOptionGroups = null) {
      if (!menuOptionGroups) return;

      // Seçili varyant için option gruplarını hazırla
      const selectedVariantOptionGroups = menuOptionGroups.filter(mog =>
        mog.variantId === this.selectedVariantId
      );

      // Her option group için bilgileri güncelle
      this.allOptionGroups.forEach(og => {
        // Varsayılan olarak seçili değil
        og.selected = 0;
        og.selected_index = 0;
        og.variant_id = null;

        // Bu option group seçili varyant için atanmış mı?
        const menuOptionGroup = selectedVariantOptionGroups.find(mog =>
          mog.optionGroupId === og.id
        );

        if (menuOptionGroup) {
          og.selected = 1;
          og.selected_index = menuOptionGroup.Index || 0;
          og.display_order = menuOptionGroup.display_order || menuOptionGroup.Index || 0;
          og.variant_id = this.selectedVariantId;
        }
      });

      // Option gruplarını sırala: Önce seçili varyanta ait olanlar, sonra diğerleri
      // Seçili olanlar display_order'a göre sıralanır
      this.allOptionGroups.sort((a, b) => {
        // Önce seçili olanları en üste getir
        if (a.selected !== b.selected) {
          return b.selected - a.selected;
        }

        // İkisi de seçiliyse display_order'a göre sırala
        if (a.selected && b.selected) {
          return a.display_order - b.display_order;
        }

        // İkisi de seçili değilse id'ye göre sırala
        return a.id - b.id;
      });

      // Sortable.js için güncelle
      this.$nextTick(() => {
        this.initOptionGroupSortable();
      });
    },

    initOptionGroupSortable() {
      const tbody = this.$refs.optionGroupsTbody;
      if (!tbody) return;

      if (this.optionGroupsSortable) {
        this.optionGroupsSortable.destroy();
      }

      // Sortable.js kullanarak option gruplarını sıralanabilir yap
      this.optionGroupsSortable = Sortable.create(tbody, {
        animation: 150,
        handle: ".move-item",
        chosenClass: "table-selected",
        easing: "cubic-bezier(1, 0, 0, 1)",
        onEnd: (evt) => {
          // Sıralama değiştiğinde seçili option grupların sırasını güncelle
          const itemEl = evt.item;
          const selectedOptionGroupId = parseInt(itemEl.querySelector('.move-item svg').getAttribute('data-og-id'));
          const selectedRows = Array.from(tbody.querySelectorAll('tr')).filter(row => {
            const optionGroupId = parseInt(row.querySelector('.move-item svg')?.getAttribute('data-og-id'));

            return this.form.selectedMenuOptionGroups.some(smog =>
              smog.optionGroupId === optionGroupId && smog.variantId === this.selectedVariantId
            );
          });
          const newIndex = selectedRows.indexOf(itemEl) + 1; // 1-based index for selected groups only

          if (isNaN(selectedOptionGroupId) || isNaN(newIndex) || newIndex < 1) {
            this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
            return;
          }

          // Seçili menu option group'u bul
          const selectedGroup = this.form.selectedMenuOptionGroups.find(smog =>
            smog.optionGroupId === selectedOptionGroupId && smog.variantId === this.selectedVariantId
          );

          if (!selectedGroup) {
            this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
            return;
          }

          const oldIndex = selectedGroup.Index || 0;

          // Sıralama değiştiğinde etkilenen option grupları güncelle
          if (newIndex > oldIndex) {
            this.form.selectedMenuOptionGroups
              .filter(item => item.variantId === this.selectedVariantId && item.Index > oldIndex && item.Index <= newIndex)
              .forEach(item => {
                item.Index--;
                item.display_order = item.Index;
              });
          } else if (newIndex < oldIndex) {
            this.form.selectedMenuOptionGroups
              .filter(item => item.variantId === this.selectedVariantId && item.Index < oldIndex && item.Index >= newIndex)
              .forEach(item => {
                item.Index++;
                item.display_order = item.Index;
              });
          }

          // Sürüklenen öğenin yeni indeksini güncelle
          selectedGroup.Index = newIndex;
          selectedGroup.display_order = newIndex;

          // allOptionGroups içindeki sıralamayı güncelle
          this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
        }
      });
    },

    toggleAllOptionGroups() {
      this.showAllOptions = !this.showAllOptions;

      if (!this.showAllOptions) {
        this.showOptionIds = false;
      }
    },

    toggleOptionIds() {
      this.showOptionIds = !this.showOptionIds;

      if (this.showOptionIds && !this.showAllOptions) {
        this.showAllOptions = true;
      }
    },

    async cloneOptionGroups(sourceVariantId) {
      if (this.selectedVariantId === sourceVariantId) return;

      const confirm = await this.mSwal.fire({
        title: "Are you sure you want to clone option groups?",
        text: `This operation will delete all selected option groups for the current variant and clone "${sourceVariantId === null ? 'Default Menu' : this.form.variants.find(v => v.id === sourceVariantId)?.name}" option groups.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, clone option groups",
        cancelButtonText: "No, cancel",
      });

      if (!confirm.isConfirmed) return;

      // Seçili varyantın mevcut option gruplarını bul ve sil
      this.form.selectedMenuOptionGroups = this.form.selectedMenuOptionGroups.filter(
        smog => smog.variantId !== this.selectedVariantId
      );

      // Kaynak varyantın option gruplarını getir
      const sourceOptionGroups = this.form.selectedMenuOptionGroups.filter(
        smog => smog.variantId === sourceVariantId
      );

      // Kaynak option gruplarını klonla ve seçili varyanta ata
      sourceOptionGroups.forEach(sourceOg => {
        const clonedOg = {
          optionGroupId: sourceOg.optionGroupId,
          variantId: this.selectedVariantId,
          Index: sourceOg.Index,
          display_order: sourceOg.display_order || sourceOg.Index,
          validDaysOfWeek: [...sourceOg.validDaysOfWeek || []]
        };

        this.form.selectedMenuOptionGroups.push(clonedOg);
      });

      // Option gruplarını güncelle
      this.prepareOptionGroups(this.form.selectedMenuOptionGroups);

      this.$toast.success(`Option groups cloned successfully!`);
    },
    //#endregion

    validate () {
      this.$v.$touch()
    },

    async reset() {
      this.form = await this.getForm();
      this.submitted = false;
      this.showErrorAlert = false;
      this.selectedMenuImage = undefined;
      this.previewMenuImage = undefined;
      this.$v.$reset();
    },

    async getRestaurantName() {
      await this.$axios
        .get(this.$backend.RESTAURANT.GET_NAME.replace("{id}", this.restaurantId), {
          params: {
            fields: 'usetoprice'
          }
        })
        .then((response) => {
          const { restaurant_name, usetoprice } = response.data;
          this.restaurantName = restaurant_name;
          this.usetoprice = usetoprice;
          this.isLoading = false;
        })
        .catch(() => { this.restaurantName = 'Not Found' })
    },

    async getAvailableCategories() {
      await this.$axios.get(this.$backend.RESTAURANT_LINKED_CATEGORIES.GET_ALL_BY_RESTAURANT_ID.replace("{restaurant_id}", this.restaurantId))
        .then((response) => (this.availableCategories = response.data));
    },

    async changedMenuCategory(category_id) {
      this.submitted = true;

      try {
        // Get menus in this category
        const menuResponse = await this.$axios.get(this.$backend.MENU_V2.LIST(this.restaurantId), { params: { category_id: category_id } });
        this.menuNames = menuResponse.data.map((m) => (m.menu_name)).join(', ');

        // Get option group templates for this category using the component
        if (this.$refs.optionGroupTemplates) {
          await this.$refs.optionGroupTemplates.fetchOptionGroupTemplates(category_id);
        }
      } finally {
        this.submitted = false;
      }
    },

    /**
     * Update the display order of a dragged option group
     * @param {Object} selectedGroup - The option group being moved
     * @param {Number} newIndex - The new index position
     */
    updateDisplayOrder: function(selectedGroup, newIndex) {
      // Sürüklenen öğenin yeni indeksini güncelle
      selectedGroup.Index = newIndex;
      selectedGroup.display_order = newIndex;

      // allOptionGroups içindeki sıralamayı güncelle
      this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
    },

    /**
     * Handle applying template from the OptionGroupTemplates component
     */
    handleApplyTemplate: function({ optionGroupIds, variantId }) {
      // Remove existing option groups for the current variant
      this.form.selectedMenuOptionGroups = this.form.selectedMenuOptionGroups.filter(
        function(item) { return item.variantId !== variantId; }
      );

      // Add new option groups from template
      let index = 1;
      var self = this;
      optionGroupIds.forEach(function(optionGroupId) {
        // Ensure optionGroupId is a number
        const ogId = parseInt(optionGroupId);
        if (isNaN(ogId)) {
          console.error('Invalid option group ID:', optionGroupId);
          return;
        }

        self.form.selectedMenuOptionGroups.push({
          optionGroupId: ogId,
          Index: index,
          display_order: index,
          validDaysOfWeek: [],
          variantId: variantId,
          selected: true
        });
        index++;
      });

      // Refresh option groups display
      this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
    },

    /**
     * Handle templates loaded from the OptionGroupTemplates component
     */
    handleTemplatesLoaded: function(templates) {
      // Store templates for reference
      this.menuTemplates = templates;
    },

    onFileChanged(files, event) {
      this.selectedMenuImage = files[0];
      this.$v.form.menu_image.$model = this.selectedMenuImage.name;

      // Yüklenen dosya geçerli kriterleri sağlıyor mu? (validations)
      if (this.$globalFunc.checkIfValid(this.$v.form, "menu_image"))
        this.previewMenuImage = URL.createObjectURL(this.selectedMenuImage);
      else {
        this.selectedMenuImage = undefined;
        this.previewMenuImage = undefined;
        this.$v.form.menu_image.$model = '';
      }
    },

    async uploadImage() {
      if (!this.selectedMenuImage) {
        return;
      }

      // Eğer resim güncellendiyse...
      const formData = new FormData();
      formData.append('menu_image', this.selectedMenuImage, this.selectedMenuImage.name);
      formData.append('menu_name', this.form.menu_name)

      await this.$axios.post(this.$backend.MENU_V2.SAVE_MENU_IMAGE(this.restaurantId), formData)
        .then((response) => {
          //Api'den gelen resim yolunu hidden inputa ata (response varsa valid olacak.)
          this.form.menu_image = response.data.data.menu_image;
          this.selectedMenuImage = null;
        })
    },

    async getAllOptionGroups() {
      this.submitted = true;

      // Tüm option grupları getir, seçili olanları filtreleme yapmadan
      var url = new URL(this.$backend.MENU_V2.GET_OPTION_GROUPS(this.restaurantId));

      const response = await this.$axios.get(url);
      this.allOptionGroups = response.data;

      // Eğer menü düzenleme modundaysa ve menü option grupları varsa, bu bilgileri kullan
      if (this.form.selectedMenuOptionGroups && this.form.selectedMenuOptionGroups.length > 0) {
        this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
      }
      this.submitted = false;
    },

    async getForm (id = null) {
      var form = {
        menu_cat: 0,
        type_id: 1,
        menu_name: '',
        description: '',
        price: 0.00,
        toPrice: 0.00,
        menu_image: '',
        custom_id: null,
        menugroups: 0,
        sold_out: false,
        internNotes: '',
        restaurant_id: this.restaurantId,

        selectedMenuOptionGroups: [],
        variants: []
      };

      if(id) {
        await this.$axios.get(this.$backend.MENU_V2.SHOW(this.restaurantId, id))
          .then((res) => {
            const menu = res.data.data;

            form.menu_cat = menu.menu_cat;
            form.type_id = menu.type_id;
            form.menu_name = menu.menu_name;
            form.description = menu.description;
            form.price = menu.price;
            form.toPrice = menu.toPrice;
            form.custom_id = menu.custom_id;
            form.menugroups = menu.menugroups;
            form.sold_out = menu.sold_out == 1;
            form.internNotes = menu.internNotes;
            form.variants = menu.variants || [];

            // Menu Image
            if(menu.menu_image_url) {
              const menu_image = menu.menu_image_url;

              form.menu_image = menu_image;
              this.previewMenuImage = menu_image;
            }

            this.itemName = menu.menu_name

            form.selectedMenuOptionGroups = menu.menu_option_groups.map(item => ({
              id: item.id,
              optionGroupId: item.option_group_id,
              validDaysOfWeek: item.available_days_of_week,
              Index: item.display_order,
              variantId: item.menu_variant_id,
              selected: true,
              optionGroup: item.option_group
            }));

            // Varyantlar için option grupları hazırla
            this.prepareOptionGroups(form.selectedMenuOptionGroups);

            // Get Menu Names by Category and load templates
            this.changedMenuCategory(menu.menu_cat);
          })
          .catch(() => {
            this.$toast.error(`${this.$store.state.errors.message}`);
            this.$router.push({ name: 'RestaurantMenuV2List' })
          })
      }

      // Form değişikliklerini kaydet
      this.commitFormChanges(form);

      return form;
    },

    async submit () {
      window.scrollTo(0,0);

      if (!this.isValid) {
        this.validate();
        return;
      }

      if (this.isEdit) {
        await this.update()
      } else {
        await this.store()
      }
    },

    async store() {
      this.submitted = true;

      try {
        await this.uploadImage();
        this.form.menugroups = this.form.selectedMenuOptionGroups.length;

        const url = this.$backend.MENU_V2.CREATE_MENU(this.restaurantId);

        const payload = {
          ...this.form,
          selectedMenuOptionGroups: this.form.selectedMenuOptionGroups.map(item => ({
            id: item.id,
            optionGroupId: item.optionGroupId,
            validDaysOfWeek: item.validDaysOfWeek,
            display_order: item.Index, // Index değerini display_order olarak gönder
            variantId: item.variantId,
          })),
        };

        const response = await this.$axios.post(url, payload);

        this.successAlertMessage = { itemName: response.data.menu_name, message: 'menu added.' }
        this.dismissSuccessAlert = this.dismissSecs
        this.reset();
      } catch (error) {
        this.errorAlertMessage = this.$store.state.errors
        this.showErrorAlert = true
      } finally {
        this.submitted = false;
      }
    },

    async update() {
      this.submitted = true;

      try {
        await this.uploadImage();
        this.form.menugroups = this.form.selectedMenuOptionGroups.length;

        const url = this.$backend.MENU_V2.UPDATE_MENU(this.restaurantId, this.itemId);

        const payload = {
          ...this.form,
          selectedMenuOptionGroups: this.form.selectedMenuOptionGroups.map(item => ({
            id: item.id,
            optionGroupId: item.optionGroupId,
            validDaysOfWeek: item.validDaysOfWeek,
            display_order: item.Index, // Index değerini display_order olarak gönder
            variantId: item.variantId,
          })),
        };

        await this.$axios.put(url, payload);

        const selectedVariantId = this.selectedVariantId;
        this.form = await this.getForm(this.itemId);

        if (this.hasVariants) {
          const variantExists = this.form.variants.some(variant => variant.id === selectedVariantId);
          this.selectVariant(variantExists ? selectedVariantId : this.form.variants[0].id);
        } else {
          this.selectVariant(null);
        }

        await this.getAllOptionGroups();

        this.successAlertMessage = { itemName: this.itemName, message: 'menu updated.' }
        this.dismissSuccessAlert = this.dismissSecs
        this.showErrorAlert = false

        // Form değişikliklerini kaydet
        this.commitFormChanges();

      } catch (error) {
        this.errorAlertMessage = this.$store.state.errors
        this.showErrorAlert = true
      } finally {
        this.submitted = false;
      }
    },

    //#region Variant
    selectVariant(variantId) {
      // If the menu has variants and trying to select default menu (null), select first variant instead
      if (this.hasVariants && variantId === null) {
        variantId = this.form.variants[0].id;
      }

      this.selectedVariantId = variantId;

      // Varyant değiştiğinde option gruplarını güncelle
      if (this.form.selectedMenuOptionGroups && this.form.selectedMenuOptionGroups.length > 0) {
        this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
      }
    },

    getSelectedVariantName() {
      const variant = this.form.variants.find(variant => variant.id === this.selectedVariantId);
      return variant ? variant.name : '';
    },

    getMenuOptionGroupsForVariant(variantId) {
      if (!this.form || !this.form.selectedMenuOptionGroups) return [];

      const menuOptionGroups = this.form.selectedMenuOptionGroups.filter(smg => smg.variantId === variantId);
      return menuOptionGroups;
    },

    getSelectedMenuOptionGroupId(ogId) {
      const selectedMenuOptionGroup = this.form.selectedMenuOptionGroups.find(smg =>
        smg.optionGroupId == ogId && smg.variantId === this.selectedVariantId
      );
      return selectedMenuOptionGroup?.id ?? 0;
    },

    async openVariantManagement() {
      if (!this.isEdit) {
        this.$toast.warning('You must save the menu first to manage variants.');
        return;
      }

      // Menüde kaydedilmemiş değişiklikler varsa önce kaydetmeyi zorunlu kıl
      if (this.hasFormChanges) {
        const result = await this.mSwal.fire({
          title: 'Unsaved Changes',
          text: 'You have unsaved changes in the menu. Please save the menu before managing variants.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Save Menu',
          cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
          // Menüyü kaydet
          await this.submit();
          if (!this.hasFormChanges) {
            this.$store.state.menuV2.variantManagementModalShow = true;
          }
        }
      } else {
        // Kaydedilmemiş değişiklik yoksa modal'ı aç
        this.$store.state.menuV2.variantManagementModalShow = true;
      }
    },

    async refreshVariants() {
      if (!this.itemId) return;

      try {
        const response = await this.$axios.get(this.$backend.MENU_V2.GET_VARIANTS(this.restaurantId, this.itemId));
        this.form.variants = response.data;

        // Menü sold out bilgisini varyantlara göre güncelle
        if (this.form.variants && this.form.variants.length > 0) {
          this.form.sold_out = this.form.variants.every(v => v.sold_out === true || v.sold_out === 1);
        } else {
          // Varyant kalmadıysa menüyü satışa açık tut
          this.form.sold_out = false;
        }

        // If the menu has variants and Default Menu is selected, select the first variant
        if (this.hasVariants) {
          // Remove all menu option groups that are associated with the default variant
          this.form.selectedMenuOptionGroups = this.form.selectedMenuOptionGroups.filter(smg => smg.variantId !== null);

          if(this.selectedVariantId === null) {
            this.selectVariant(this.form.variants[0].id);
          }

          // Update menu price to the lowest variant price if there are variants
          const lowestPrices = this.form.variants.reduce(
            (acc, variant) => ({
              price: Math.min(acc.price, variant.price),
              toPrice: Math.min(acc.toPrice, variant.toPrice),
            }),
            { price: Infinity, toPrice: Infinity },
          );

          this.form.price = lowestPrices.price;
          this.form.toPrice = lowestPrices.toPrice;
          this.$v.form.price.$touch();
          this.$v.form.toPrice.$touch();
        } else {
          this.selectVariant(null);
        }

        // Variant güncellemeleri API'de yapıldığı için sadece variant verilerini commit et
        // Bu sayede menüdeki diğer unsaved changes korunur
        this.commitVariantChanges();
      } catch (error) {
        console.error('Failed to refresh variants:', error);
      }
    },

    variantDeleted(variantId) {
      // Remove all menu option groups that are associated with the deleted variant
      this.form.selectedMenuOptionGroups = this.form.selectedMenuOptionGroups.filter(smg => smg.variantId !== variantId);

      this.refreshVariants();
    },
    //#endregion

    //#region Variant Option Price
    openVariantOptionPriceModal(option) {
      if(this.hasFormChanges) {
        this.$toast.warning('You must save the menu first to manage variant option prices.');
        return;
      }

      this.selectedOption = option;
      this.$store.state.menuV2.variantOptionPriceModalShow = true;
    },

    getVariantsWithOptionPrices(option) {
      if (!option) return [];

      // Option'ı içeren menuOptionGroup'ları bul
      const relevantGroups = this.form.selectedMenuOptionGroups.filter(
        group => group.optionGroup.options.some(opt => opt.id === option.id)
      );

      // Sonuç dizisi
      const result = relevantGroups.reduce((acc, group) => {
        const variantId = group.variantId;
        const variant = this.form.variants.find(v => v.id === variantId);

        if (variant) {
          const variantCopy = Object.assign({}, variant);

          const optionPrice = group.optionGroup.options.find(opt => opt.id === option.id)
            ?.menu_variant_option_prices?.find(price => price.menu_variant_id === variantId);

          variantCopy.option_price = optionPrice ? optionPrice.price : null;
          variantCopy.option_toPrice = optionPrice ? optionPrice.toPrice : null;

          // Sonuca ekle (aynı varyant birden fazla kez eklenmemesi için kontrol et)
          if (!acc.some(v => v.id === variantCopy.id)) {
            return [...acc, variantCopy];
          }
        }

        return acc;
      }, []);

      return result;
    },

    async refreshOptionPrices(variants) {
      if(this.itemId) {
        this.form = await this.getForm(this.itemId);
        this.prepareOptionGroups(this.form.selectedMenuOptionGroups);
      }
    },
    //#endregion

    //#region Form Change Tracking
    commitFormChanges(form = this.form) {
      this.lastSavedForm = JSON.parse(JSON.stringify(form));
      this.hasFormChanges = false;
    },

    /**
     * Variant değişikliklerinden etkilenen alanları commit eder, diğer form değişikliklerini korur
     * Variant güncellemeleri API'de yapıldığı için bu değişiklikler unsaved changes olarak görünmemeli
     */
    commitVariantChanges() {
      if (!this.lastSavedForm) {
        // Eğer hiç kaydedilmemişse, tüm formu commit et
        this.commitFormChanges();
        return;
      }

      // Variant verilerini güncelle
      this.lastSavedForm.variants = JSON.parse(JSON.stringify(this.form.variants));
      
      // Variant değişikliklerinden etkilenen price ve toPrice değerlerini commit et
      // (refreshVariants içinde lowestPrices hesaplanıyor ve form.price/toPrice güncelleniyor)
      this.lastSavedForm.price = this.form.price;
      this.lastSavedForm.toPrice = this.form.toPrice;
      
      // Variant değişikliklerinden etkilenen sold_out değerini commit et
      // (refreshVariants içinde variant sold_out durumuna göre form.sold_out güncelleniyor)
      this.lastSavedForm.sold_out = this.form.sold_out;
      
      // Silinen varyanta bağlı option group'ları da commit et
      // (variantDeleted içinde form.selectedMenuOptionGroups filtreleniyor)
      this.lastSavedForm.selectedMenuOptionGroups = JSON.parse(JSON.stringify(this.form.selectedMenuOptionGroups));
      
      // Form watch otomatik olarak hasFormChanges'ı güncelleyecek
      // Ancak menüdeki diğer değişiklikler korunmalı
    },

    getChangedFields: function(oldObj, newObj) {
      const changedFields = {};

      // Compare basic properties
      for (const key in oldObj) {
        if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
          changedFields[key] = { old: oldObj[key], new: newObj[key] };
        }
      }

      // Check for option group changes
      if (oldObj.selectedMenuOptionGroups && newObj.selectedMenuOptionGroups) {
        const optionGroupChanges = this.detectArrayChanges(
          oldObj.selectedMenuOptionGroups,
          newObj.selectedMenuOptionGroups,
          'id'
        );
        if (optionGroupChanges.length > 0) {
          changedFields.optionGroups = optionGroupChanges;
        }
      }

      // Check for variant changes
      if (oldObj.variants && newObj.variants) {
        const variantChanges = this.detectArrayChanges(
          oldObj.variants,
          newObj.variants,
          'id'
        );
        if (variantChanges.length > 0) {
          changedFields.variants = variantChanges;
        }
      }

      return changedFields;
    },

    detectArrayChanges(oldArray, newArray, idField) {
      const changes = [];

      // Find added or modified items
      newArray.forEach(newItem => {
        const oldItem = oldArray.find(item => item[idField] === newItem[idField]);

        if (!oldItem) {
          changes.push({ type: 'added', item: newItem });
        } else if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
          changes.push({
            type: 'modified',
            id: newItem[idField],
            changes: this.getObjectDifferences(oldItem, newItem)
          });
        }
      });

      // Find removed items
      oldArray.forEach(oldItem => {
        const stillExists = newArray.some(item => item[idField] === oldItem[idField]);
        if (!stillExists) {
          changes.push({ type: 'removed', id: oldItem[idField] });
        }
      });

      return changes;
    },

    getObjectDifferences(oldObj, newObj) {
      const differences = {};

      // Check all properties in new object
      for (const key in newObj) {
        if (typeof oldObj[key] === 'undefined') {
          differences[key] = { added: newObj[key] };
        } else if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
          differences[key] = {
            from: oldObj[key],
            to: newObj[key]
          };
        }
      }

      // Check for deleted properties
      for (const key in oldObj) {
        if (typeof newObj[key] === 'undefined') {
          differences[key] = { removed: oldObj[key] };
        }
      }

      return differences;
    },
    //#endregion
  }
}
</script>

<style scoped>
.disable-item {
  pointer-events: none;
  opacity: 0.6;
}
.enable-item {
  pointer-events: auto;
  opacity: 1;
}
.enable-item, .move-item {
  vertical-align: top !important;
}
.selected-item {
  border-left: 5px solid #2eb85c;
}
</style>

<style>
/* Menu Option Group Custom Checkbox */
.custom-checkbox-label {
  font-size: 18px;
  font-weight: 500;
}
.custom-control-input:checked ~ .custom-checkbox-label::before {
  border-color: #2eb85c;
  background-color: #2eb85c;
}


/* Available Days */
.day-group .form-check {
  padding-left: 0 !important;
}
.day-group .form-check-input {
  display: none;
}
.day-group .form-check-label {
  display: flex;
  align-items: center;
  justify-content: center;

  font-weight: normal;
  width: 32px !important;
  height: 32px !important;
  cursor: pointer;
  padding: 0.275rem;
  border: 1px solid rgba(60,60,60,.26);
  border-radius: 50%;
}
.day-group input[type=checkbox]:checked + .form-check-label {
  background-color: #d5f1de;
  border-color: #c4ebd1;
}


/* Option Groups */
.option-group-info {
  background-color: #f7f7f7;
  padding: 10px;
  border-radius: 5px;
}

.options-container {
  max-height: 200px;
  overflow-y: auto;
}

.option-item {
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 5px;
  /* cursor: pointer; */
}

.option-item:hover {
  background-color: #f7f7f7;
}

.option-inactive {
  opacity: 0.6;
  pointer-events: none;
}


/* Variants */
.variant-selector {
  background-color: #f7f7f7;
  padding: 20px;
  border-radius: 5px;
}

.variant-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: fit-content; /* Sadece içerik kadar genişlik */
  max-width: 100%; /* Ekrandan taşmayı önle */
}

.variant-tab {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  cursor: pointer;
  flex: 0 1 auto; /* flex-grow:0, flex-shrink:1, flex-basis:auto */
  min-width: 240px;
  max-width: 240px;
  width: auto; /* İçeriğe göre doğal genişlik */
}

.variant-tab.active {
  background-color: #99d88e;
  color: #fff;
  border-color: #74a36b !important;
  opacity: 1 !important;
}

.variant-tab.active .description {
  color: #fff !important;
}

.variant-tab.inactive {
  opacity: 0.6;
  border-color: #e55353;
}

.price-badge {
  font-size: 14px;
  padding: 2px 5px;
  border-radius: 5px;
}



/* Option Groups Section Styles */
.option-groups-section {
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 260px); /* Adjust based on your layout */
  min-height: 400px;
  border: 1px solid #e4e7ea;
  border-radius: 4px;
  overflow: hidden;
}

.option-groups-header {
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  border-bottom: 1px solid #e4e7ea;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 10px 15px;
  flex-shrink: 0;
}

.option-groups-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
}

@media print {
  .option-groups-section {
    height: auto;
    min-height: auto;
  }

  .option-groups-header {
    position: relative;
  }

  .option-groups-content {
    overflow: visible;
  }
}
</style>
